import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useHookFormMask } from "use-mask-input";
import { jwtDecode } from "jwt-decode";

import "./forms.css";
import Badge from "../badge/badge";
import Button from "../button/button";
import KeyIcon from "../../assets/icons/key.svg";
import EditIcon from "../../assets/icons/edit.svg";
import { PHONE_REGEX } from "../../utils/pagesMapper";
import ErrorMessage from "../forms/errorMessage/errorMessage";
import { getCookie } from "../../utils/cookies";
import { useInstitution } from "../../context/InstitutionContext";
import { createUser, updateUser } from "../../services/academetrics";
import CheckedCircle from "../../assets/icons/checked-circle.svg";

const CreateInstitution = ({ isRegister = true, user = null }) => {
  const navigate = useNavigate();
  const { institution } = useInstitution();
  
  const [currentLoggedUser, setCurrentLoggedUser] = useState(null);

  const userFormFormatted = () => {
    return {
      firstName: user?.firstName,
      lastName: user?.lastName,
      institution: user?.institution,
      role: user?.role,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({ values: userFormFormatted() });

  const registerWithMask = useHookFormMask(register);

  const [loading, setLoading] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user?.role || 'Financial');
  const [canImportFiles, setCanImportFiles] = useState(
    user?.role === 'AcademicPlus' || user?.role === 'FinancialPlus'
  );

  // Busca o usuário logado do token JWT
  useEffect(() => {
    try {
      const token = getCookie("token");
      if (token) {
        const decoded = jwtDecode(token);
        
        // Tenta diferentes formatos de claims
        const userId = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] 
          || decoded["sub"] 
          || decoded["userId"]
          || decoded["nameid"];
          
        const userEmail = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"]
          || decoded["email"]
          || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
        
        const loggedUser = {
          id: userId,
          email: userEmail
        };
        
        setCurrentLoggedUser(loggedUser);
      }
    } catch (error) {
      console.log("Erro ao decodificar token:", error);
    }
  }, []);

  // Atualiza o selectedRole quando o user for carregado
  useEffect(() => {
    if (user?.role) {
      setSelectedRole(user.role);
      setCanImportFiles(user.role === 'AcademicPlus' || user.role === 'FinancialPlus');
    }
  }, [user]);

  // Verifica se está editando o próprio usuário
  const isEditingSelf = !isRegister && currentLoggedUser && user && (
    String(currentLoggedUser.id) === String(user.id) ||
    currentLoggedUser.email === user.email
  );

  const onSubmit = (data) => {
    let updatedRole;

    if (isRegister) {
      // No cadastro, processa role e canImportFiles
      updatedRole = selectedRole;

      if (selectedRole === 'Academic' && canImportFiles) {
        updatedRole = 'AcademicPlus';
      }

      if (selectedRole === 'Financial' && canImportFiles) {
        updatedRole = 'FinancialPlus';
      }
    } else {
      // Na edição, usa a role selecionada ou mantém a original
      updatedRole = selectedRole;
      
      if (selectedRole === 'Academic' && canImportFiles) {
        updatedRole = 'AcademicPlus';
      }

      if (selectedRole === 'Financial' && canImportFiles) {
        updatedRole = 'FinancialPlus';
      }
    }

    const formattedData = {
      ...data,
      role: updatedRole
    };

    isRegister ? handleRegister(formattedData) : handleEdit(formattedData);
  };

  const roleDescriptions = {
    'Admin': 'Administrador com controle total do sistema',
    'Manager': 'Acesso completo a todas as funcionalidades do sistema, porém não consegue editar informações do sistema',
    'Academic': 'Acesso aos dados acadêmicos',
    'Financial': 'Acesso aos dados financeiros',
    'Marketing': 'Acesso aos dados de marketing',
    'Operator': 'Acesso apenas a importação de arquivos'
  };

  const handleRegister = async (data) => {
    setLoading(true);

    try {
      await createUser({ ...data, institutionId: institution.id });
      navigate("/app/usuarios");
    } catch (error) {
      console.log("@error", error);
    }

    setLoading(false);
  };

  const handleEdit = async (data) => {
    setLoading(true);

    try {
      await updateUser({ ...data, userId: user?.id });
      navigate("/app/usuarios");
    } catch (error) {
      console.log("@error", error);
    }

    setLoading(false);
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      'Admin': 'Admin',
      'Manager': 'Gerente',
      'Academic': 'Acadêmico',
      'AcademicPlus': 'Acadêmico com importação de arquivo',
      'Financial': 'Financeiro',
      'FinancialPlus': 'Financeiro com importação de arquivo',
      'Marketing': 'Marketing',
      'Operator': 'Operador'
    };
    return roleMap[role] || role;
  };

  const getRoleDisplayName = (roleKey) => {
    const displayNames = {
      'Admin': 'Admin',
      'Manager': 'Gerente',
      'Academic': 'Acadêmico',
      'Financial': 'Financeiro',
      'Marketing': 'Marketing',
      'Operator': 'Operador'
    };
    return displayNames[roleKey] || roleKey;
  };

  return (
    <div className="institution-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <legend>Informações do Usuário</legend>

          {!isRegister && user && selectedRole && (
            <div className="form-row" style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                backgroundColor: '#f5f5f5',
                padding: '12px 16px',
                borderRadius: '6px',
                width: '100%'
              }}>
                <span style={{ fontWeight: '600', fontSize: '12px', color: '#0962ad' }}>
                  Perfil Atual:
                </span>
                <Badge text={getRoleLabel(selectedRole)} color="blue" />
                {!isEditingSelf && (
                  <img 
                    src={EditIcon} 
                    alt="Editar perfil" 
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      cursor: 'pointer',
                      marginLeft: 'auto'
                    }}
                    onClick={() => setIsEditingRole(!isEditingRole)}
                  />
                )}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="Nome"
                {...register("firstName", {
                  required: "O nome é obrigatório."
                })}
              />

              <ErrorMessage message={errors.firstName?.message} />
            </div>

            <div className="form-group">
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Sobrenome"
                {...register("lastName", {
                  required: "O sobrenome é obrigatório."
                })}
              />

              <ErrorMessage message={errors.lastName?.message} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                placeholder="Telefone"
                {...registerWithMask("phoneNumber", ["(99) 99999-9999"], {
                  required: "O telefone é obrigatório.",
                  pattern: {
                    value: PHONE_REGEX,
                    message: "O telefone é inválido."
                  }
                })}
              />
              <ErrorMessage message={errors.phoneNumber?.message} />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Acesso</legend>

          <div className="form-row">
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="E-mail"
                {...register("email", {
                  required: "O e-mail é obrigatório.",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "O e-mail é inválido."
                  }
                })}
              />
              <ErrorMessage message={errors.email?.message} />
            </div>

            <div className="form-group">
              <img src={KeyIcon} alt="Senha" className="password-icon" />

              <input
                type="password"
                id="password"
                name="password"
                placeholder="Senha"
                {...register("password", isRegister && {
                  required: "A senha é obrigatória.",
                  minLength: {
                    value: 6,
                    message: "A senha deve ter no mínimo 6 caracteres."
                  }
                })}
              />
              <ErrorMessage message={errors.password?.message} />
            </div>
          </div>

        </fieldset>

        {isRegister && (
          <fieldset style={{ marginTop: '20px' }}>
            <legend>Perfil do Usuário</legend>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {Object.entries(roleDescriptions).map(([roleKey, description]) => (
                <div 
                  key={roleKey}
                  style={{
                    backgroundColor: selectedRole === roleKey 
                      ? '#e3f2fd' : '#f5f5f5',
                    padding: '16px',
                    borderRadius: '6px',
                    border: selectedRole === roleKey 
                      ? '2px solid #0962ad' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setSelectedRole(roleKey)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <input
                      type="radio"
                      name="roleSelection"
                      value={roleKey}
                      checked={selectedRole === roleKey}
                      onChange={() => setSelectedRole(roleKey)}
                      style={{ 
                        marginTop: '4px',
                        width: '16px', 
                        height: '16px',
                        accentColor: '#0962ad',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '14px', 
                        color: '#0962ad',
                        marginBottom: '4px'
                      }}>
                        {getRoleDisplayName(roleKey)}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        lineHeight: '1.4'
                      }}>
                        {description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {(selectedRole === 'Academic' || 
                selectedRole === 'Financial') && (
                <div style={{ marginTop: '8px' }}>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={canImportFiles}
                        onChange={(e) => setCanImportFiles(e.target.checked)}
                      />
                      Permitir importação de arquivos
                    </label>
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#666',
                    marginTop: '8px',
                    marginLeft: '24px'
                  }}>
                    Usuário poderá importar arquivos de dados para o sistema
                  </div>
                </div>
              )}
            </div>
          </fieldset>
        )}

        {!isRegister && isEditingRole && !isEditingSelf && (
          <fieldset style={{ marginTop: '20px' }}>
            <legend>Editar Perfil</legend>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {Object.entries(roleDescriptions).map(([roleKey, description]) => (
                <div 
                  key={roleKey}
                  style={{
                    backgroundColor: selectedRole === roleKey || 
                      (selectedRole === 'AcademicPlus' && roleKey === 'Academic') ||
                      (selectedRole === 'FinancialPlus' && roleKey === 'Financial')
                      ? '#e3f2fd' : '#f5f5f5',
                    padding: '16px',
                    borderRadius: '6px',
                    border: selectedRole === roleKey || 
                      (selectedRole === 'AcademicPlus' && roleKey === 'Academic') ||
                      (selectedRole === 'FinancialPlus' && roleKey === 'Financial')
                      ? '2px solid #0962ad' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setSelectedRole(roleKey)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <input
                      type="radio"
                      name="roleSelection"
                      value={roleKey}
                      checked={selectedRole === roleKey || 
                        (selectedRole === 'AcademicPlus' && roleKey === 'Academic') ||
                        (selectedRole === 'FinancialPlus' && roleKey === 'Financial')}
                      onChange={() => setSelectedRole(roleKey)}
                      style={{ 
                        marginTop: '4px',
                        width: '16px', 
                        height: '16px',
                        accentColor: '#0962ad',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '14px', 
                        color: '#0962ad',
                        marginBottom: '4px'
                      }}>
                        {getRoleDisplayName(roleKey)}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        lineHeight: '1.4'
                      }}>
                        {description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {(selectedRole === 'Academic' || selectedRole === 'AcademicPlus' || 
                selectedRole === 'Financial' || selectedRole === 'FinancialPlus') && (
                <div style={{ marginTop: '8px' }}>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={canImportFiles}
                        onChange={(e) => setCanImportFiles(e.target.checked)}
                      />
                      Permitir importação de arquivos
                    </label>
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#666',
                    marginTop: '8px',
                    marginLeft: '24px'
                  }}>
                    Usuário poderá importar arquivos de dados para o sistema
                  </div>
                </div>
              )}
            </div>
          </fieldset>
        )}

        <div className="form-actions" style={{ marginTop: (isEditingRole || isRegister) ? '20px' : '0' }}>
          <Button
            text={isRegister ? "Cadastrar Usuário" : "Editar Usuário"}
            variation="primary"
            size="big"
            icon={CheckedCircle}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default CreateInstitution;
